import {
  Alert,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle, QueryState } from '../../ui/components';
import { formatDate } from '../../ui/format';
import { useWorkplace } from '../access';
import { InviteError, useInvite, useRevokeMember } from '../accessActions';
import { useInviteText } from '../inviteText';
import { unwrap, useDb } from '../supabase';
import { AdminOnly } from './AdminOnly';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Member {
  id: string;
  company_id: string;
  user_id: string;
  created_at: string;
  revoked_at: string | null;
}

/** A3 Office users (Admin): DPAs per company; give office access (invite) and end it. */
export function AdminOfficeUsersPage() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  const { access } = useWorkplace();
  const invite = useInvite();
  const revoke = useRevokeMember();
  const inviteText = useInviteText();
  const [form, setForm] = useState({ companyId: '', fullName: '', email: '' });
  const [ending, setEnding] = useState<Member | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const members = useQuery({
    queryKey: ['admin', 'members'],
    enabled: access.isAdmin,
    queryFn: async () => {
      const rows = unwrap(
        await db
          .from('company_members')
          .select('id, company_id, user_id, role, created_at, revoked_at')
          .order('created_at'),
      );
      const people = unwrap(
        await db
          .from('profiles')
          .select('id, full_name, email')
          .in(
            'id',
            rows.map((r) => r.user_id),
          ),
      );
      return { rows, people: new Map(people.map((p) => [p.id, p])) };
    },
  });
  const companyName = (id: string) => access.companies.find((c) => c.id === id)?.name ?? '';
  const personName = (id: string) => {
    const name = members.data?.people.get(id)?.full_name ?? '';
    return name.trim() === '' ? t('crew.former') : name;
  };
  const emailOk = EMAIL.test(form.email.trim());
  const ready = form.companyId !== '' && form.fullName.trim().length >= 2 && emailOk;
  const inviteError =
    invite.error === null
      ? null
      : invite.error instanceof InviteError
        ? t(`invite.errors.${invite.error.code}`)
        : t('common.saveFailed', { message: invite.error.message });

  const give = () => {
    const request = {
      kind: 'office' as const,
      email: form.email.trim(),
      fullName: form.fullName.trim(),
      companyId: form.companyId,
    };
    setDone(null);
    invite.mutate(request, {
      onSuccess: (result) => {
        setDone(
          `${t('admin.given', { name: request.fullName, company: companyName(request.companyId) })} ${inviteText(result, request.email.toLowerCase())}`,
        );
        setForm({ companyId: form.companyId, fullName: '', email: '' });
      },
    });
  };

  return (
    <AdminOnly>
      <PageTitle title={t('admin.officeUsersTitle')} />
      <Stack gap="lg">
        {done === null ? null : (
          <Alert color="green" role="status">
            {done}
          </Alert>
        )}
        <Card withBorder>
          <Stack>
            <Title order={2} size="h3">
              {t('admin.inviteTitle')}
            </Title>
            <Text size="sm">{t('admin.inviteBody')}</Text>
            <Group align="flex-start">
              <Select
                label={t('admin.company')}
                data={access.companies.map((c) => ({ value: c.id, label: c.name }))}
                value={form.companyId === '' ? null : form.companyId}
                onChange={(value) => setForm({ ...form, companyId: value ?? '' })}
              />
              <TextInput
                label={t('crew.fullName')}
                value={form.fullName}
                maxLength={120}
                onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
              />
              <TextInput
                label={t('crew.email')}
                type="email"
                value={form.email}
                maxLength={254}
                error={form.email !== '' && !emailOk ? t('crew.emailInvalid') : undefined}
                onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
              />
            </Group>
            <Group>
              <Button disabled={!ready} loading={invite.isPending} onClick={give}>
                {t('admin.invite')}
              </Button>
            </Group>
            {inviteError === null ? null : (
              <Alert color="red" role="alert">
                {inviteError}
              </Alert>
            )}
          </Stack>
        </Card>
        <QueryState loading={members.isPending} error={members.error}>
          {() => (
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('admin.person')}</Table.Th>
                  <Table.Th>{t('admin.company')}</Table.Th>
                  <Table.Th>{t('admin.role')}</Table.Th>
                  <Table.Th>{t('admin.access')}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(members.data?.rows ?? []).map((m) => {
                  const person = members.data?.people.get(m.user_id);
                  return (
                    <Table.Tr key={m.id}>
                      <Table.Td>
                        <Text fw={600}>{personName(m.user_id)}</Text>
                        <Text size="sm">{person?.email ?? ''}</Text>
                      </Table.Td>
                      <Table.Td>{companyName(m.company_id)}</Table.Td>
                      <Table.Td>{t('admin.dpa')}</Table.Td>
                      <Table.Td>
                        {m.revoked_at === null
                          ? t('admin.since', { date: formatDate(m.created_at) })
                          : t('admin.ended', { date: formatDate(m.revoked_at) })}
                      </Table.Td>
                      <Table.Td>
                        {m.revoked_at === null ? (
                          <Button
                            size="xs"
                            variant="default"
                            aria-label={`${t('admin.endAccess')}: ${personName(m.user_id)}`}
                            onClick={() => {
                              setReason('');
                              setEnding(m);
                            }}
                          >
                            {t('admin.endAccess')}
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </QueryState>
      </Stack>
      <Modal
        opened={ending !== null}
        onClose={() => setEnding(null)}
        title={ending === null ? '' : t('admin.endTitle', { name: personName(ending.user_id) })}
      >
        {ending === null ? null : (
          <Stack>
            <Text>
              {t('admin.endBody', {
                name: personName(ending.user_id),
                company: companyName(ending.company_id),
              })}
            </Text>
            <TextInput
              label={t('crew.reason')}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            {revoke.error === null ? null : (
              <Alert color="red" role="alert">
                {t('common.saveFailed', { message: revoke.error.message })}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEnding(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                color="red"
                loading={revoke.isPending}
                onClick={() =>
                  revoke.mutate(
                    { memberId: ending.id, reason: reason.trim() === '' ? null : reason.trim() },
                    {
                      onSuccess: () => {
                        setDone(t('admin.endedNow', { name: personName(ending.user_id) }));
                        setEnding(null);
                      },
                    },
                  )
                }
              >
                {t('admin.endAccess')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </AdminOnly>
  );
}
